import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { captureError } from '../lib/sentry';
import {
  subscribeToNotifications,
  subscribeToNudges,
  markNudgeRead,
  markNudgesReadBatch,
} from '../services/eventService';
import type { NudgeNotification } from '../services/eventService';
import { updateNotificationsSeenAt, subscribeToUserNotificationDoc } from '../services/userService';

/** Statuses that warrant a user notification. */
export const NOTIFICATION_STATUSES = [
  'voting_started',
  'candidates_ready',
  'complementary_ready',
  'generated',
  'error',
] as const;

export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const NOTIFICATION_LABELS: Record<NotificationStatus, string> = {
  voting_started: 'Voting has started — candidates locked',
  candidates_ready: 'Venue suggestions ready — vote now',
  complementary_ready: 'Activity suggestions ready — vote now',
  generated: 'Your plan is ready!',
  error: 'Something went wrong',
};

export interface EventNotification {
  eventId: string;
  eventName: string;
  status: NotificationStatus;
}

export type { NudgeNotification } from '../services/eventService';

export type UnifiedNotification =
  | (EventNotification & { notificationType: 'event' })
  | (NudgeNotification & { notificationType: 'nudge' });

/**
 * useNotifications — subscribes to the current user's events and surfaces
 * any that are in a "needs attention" status and haven't been marked as read.
 *
 * Read-state is persisted in `users/{uid}.notificationsSeenAt` as a flat
 * Record<eventId, lastSeenStatus>.  An event is "unread" when its current
 * status differs from the stored value (or there is no stored value yet).
 */
export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<EventNotification[]>([]);
  const [nudgeNotifications, setNudgeNotifications] = useState<NudgeNotification[]>([]);
  const [seenAt, setSeenAt] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // Subscribe to events where the user is host
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- terminal state when user is absent; no subscription to drive a later update
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsub = subscribeToNotifications(
      user.uid,
      (rawEvents) => {
        const relevant: EventNotification[] = [];
        rawEvents.forEach(({ id, name, status }) => {
          let notificationStatus: NotificationStatus | null = null;

          // M-175: map 'ready' status to 'voting_started' notification
          if (status === 'ready') {
            notificationStatus = 'voting_started';
          } else if ((NOTIFICATION_STATUSES as readonly string[]).includes(status)) {
            notificationStatus = status as NotificationStatus;
          }

          if (notificationStatus) {
            relevant.push({ eventId: id, eventName: name, status: notificationStatus });
          }
        });
        setNotifications(relevant);
        setLoading(false);
      },
      (err) => {
        captureError(err, { hook: 'useNotifications', phase: 'events-subscription' });
        console.error('[useNotifications] events subscription error:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  // Subscribe to the user doc for the notificationsSeenAt field
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- terminal state when user is absent; no subscription to drive a later update
      setSeenAt({});
      return;
    }

    const unsub = subscribeToUserNotificationDoc(
      user.uid,
      (incoming) => {
        setSeenAt(incoming);
      },
      (err) => {
        captureError(err, { hook: 'useNotifications', phase: 'user-doc-subscription' });
        console.error('[useNotifications] user doc subscription error:', err);
      }
    );

    return () => unsub();
  }, [user]);

  // M-169: Subscribe to nudge notifications subcollection
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- terminal state when user is absent; no subscription to drive a later update
      setNudgeNotifications([]);
      return;
    }

    const unsub = subscribeToNudges(
      user.uid,
      (nudges) => {
        setNudgeNotifications(nudges);
      },
      (err) => {
        captureError(err, { hook: 'useNotifications', phase: 'nudge-subscription' });
        console.error('[useNotifications] nudge subscription error:', err);
      }
    );

    return () => unsub();
  }, [user]);

  /** Unread = notification-worthy status that differs from the last-seen status. */
  const unreadEvents = notifications.filter((n) => seenAt[n.eventId] !== n.status);

  /** M-169: Combine event and nudge notifications into unified list */
  const unread: UnifiedNotification[] = useMemo(
    () => [
      ...unreadEvents.map((n) => ({ ...n, notificationType: 'event' as const })),
      ...nudgeNotifications.map((n) => ({ ...n, notificationType: 'nudge' as const })),
    ],
    [unreadEvents, nudgeNotifications]
  );

  const unreadCount = unread.length;

  /** Mark a single event notification as read. */
  const markRead = useCallback(
    async (eventId: string, status?: NotificationStatus, nudgeId?: string) => {
      if (!user) return;
      try {
        // M-169: If nudgeId provided, mark nudge as read
        if (nudgeId) {
          await markNudgeRead(user.uid, nudgeId);
        } else if (status) {
          // Original event notification logic
          await updateNotificationsSeenAt(user.uid, { [`notificationsSeenAt.${eventId}`]: status });
        }
      } catch (err) {
        captureError(err, { hook: 'useNotifications', phase: 'markRead' });
        console.error('[useNotifications] markRead failed:', err);
      }
    },
    [user]
  );

  /** Mark all current unread notifications as read. */
  const markAllRead = useCallback(async () => {
    if (!user || unread.length === 0) return;
    try {
      const updates: Record<string, string> = {};
      // Mark event notifications
      unreadEvents.forEach((n) => {
        updates[`notificationsSeenAt.${n.eventId}`] = n.status;
      });
      if (Object.keys(updates).length > 0) {
        await updateNotificationsSeenAt(user.uid, updates);
      }

      // M-169: Mark nudge notifications
      if (nudgeNotifications.length > 0) {
        await markNudgesReadBatch(
          user.uid,
          nudgeNotifications.map((n) => n.id)
        );
      }
    } catch (err) {
      captureError(err, { hook: 'useNotifications', phase: 'markAllRead' });
      console.error('[useNotifications] markAllRead failed:', err);
    }
  }, [user, unread, unreadEvents, nudgeNotifications]);

  return { notifications, unread, unreadCount, markRead, markAllRead, loading };
};
